import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../../services/api';
import * as Sentry from "@sentry/react";
import Sidebar from '../../components/layout/Sidebar';
import { useNotification } from '../../Notifications';
import { useAuth } from '../../components/auth/AuthContext';

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'my-account' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'my-account' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'my-account',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Error message constants
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'USER_NOT_FOUND': 'Please refresh and try again',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'RATE_LIMIT_EXCEEDED': 'Too many requests. Please wait a moment before trying again',
  'REQUEST_TOO_LARGE': 'Request data is too large',
  'PROFILE_FETCH_FAILED': 'Unable to load profile data. Please try again.',
  'PROFILE_UPDATE_FAILED': 'Unable to update profile. Please try again.',
  'PROFILE_COMPLETENESS_CHECK_FAILED': 'Unable to check profile status. Please refresh the page.',
  'DUPLICATE_NAME': 'An API key with this name already exists',
  'LIMIT_EXCEEDED': 'Maximum API keys limit reached',
  'KEY_GENERATION_FAILED': 'Failed to generate API key. Please try again',
  'KEY_NOT_FOUND': 'API key not found',
  'API_KEYS_FETCH_FAILED': 'Unable to load API keys. Please try again.',
  'API_KEY_DELETE_FAILED': 'Unable to delete API key. Please try again.',
  'INCOMPLETE_PROFILE': 'Complete your profile to access API keys',
  'NETWORK_ERROR': 'Unable to connect. Please check your internet connection',
  'CONNECTION_PROBLEM': 'Unable to connect. Please check your internet connection',
  'DATABASE_ERROR': 'Service temporarily unavailable. Please try again',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again',
  'INVALID_DATA': 'Invalid input data'
};

// Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return ERROR_MESSAGES['SERVER_ERROR'];
  }
  
  // Check error.error first (interceptor's user-friendly message)
  if (error.error && typeof error.error === 'string') {
    return error.error;
  }
  
  // Check direct error code
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Check HTTP status codes
  if (error.status >= 500) {
    return ERROR_MESSAGES['SERVER_ERROR'];
  }
  if (error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  return ERROR_MESSAGES['SERVER_ERROR'];
};
// Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Avatar generation logic
const generateAvatar = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return "U";
  
  const nameParts = fullName.trim().split(" ");
  
  if (nameParts.length >= 2) {
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  } else {
    return fullName.substring(0, 2).toUpperCase();
  }
};

// Validate redirect paths to prevent open redirect attacks
const validateRedirectPath = (path) => {
  if (!path || typeof path !== 'string') {
    return '/dashboard';
  }

  const cleanPath = path.trim();

  if (!cleanPath.startsWith('/')) {
    return '/dashboard';
  }

  if (cleanPath.includes('://') || cleanPath.includes('\\')) {
    return '/dashboard';
  }

  if (cleanPath.startsWith('//')) {
    return '/dashboard';
  }

  if (cleanPath.toLowerCase().startsWith('javascript:') || 
      cleanPath.toLowerCase().startsWith('data:')) {
    return '/dashboard';
  }

  return cleanPath;
};

// Enhanced validation functions
const validateName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'Name is required' };
  }
  const trimmedName = name.trim();
  if (trimmedName.length === 0) {
    return { valid: false, error: 'Name is required' };
  }
  if (trimmedName.length > 100) {
    return { valid: false, error: 'Name must be 100 characters or less' };
  }
  if (!/^[A-Za-z\s\-']+$/.test(trimmedName)) {
    return { valid: false, error: 'Name contains invalid characters' };
  }
  if (trimmedName.length < 2) {
    return { valid: false, error: 'Name must be at least 2 characters' };
  }
  return { valid: true };
};

const validatePhoneNumber = (phone) => {
  if (!phone) return { valid: true };
  if (typeof phone !== 'string') {
    return { valid: false, error: 'Invalid phone number format' };
  }
  const trimmedPhone = phone.trim();
  if (trimmedPhone.length === 0) return { valid: true };
  if (trimmedPhone.length > 20) {
    return { valid: false, error: 'Phone number too long' };
  }
  if (!/^[0-9+\s\-()]{6,20}$/.test(trimmedPhone)) {
    return { valid: false, error: 'Please enter a valid phone number (6-20 characters)' };
  }
  return { valid: true };
};

const validatePostalCode = (code) => {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: 'Postal code is required' };
  }
  const trimmedCode = code.trim();
  if (trimmedCode.length === 0) {
    return { valid: false, error: 'Postal code is required' };
  }
  if (trimmedCode.length > 20) {
    return { valid: false, error: 'Postal code too long' };
  }
  if (!/^[A-Za-z0-9\-\s]+$/.test(trimmedCode)) {
    return { valid: false, error: 'Invalid postal code format' };
  }
  return { valid: true };
};

const validateAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return { valid: false, error: 'Address is required' };
  }
  const trimmedAddress = address.trim();
  if (trimmedAddress.length === 0) {
    return { valid: false, error: 'Address is required' };
  }
  if (trimmedAddress.length > 255) {
    return { valid: false, error: 'Address too long' };
  }
  return { valid: true };
};

const validateAPIKeyName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'API key name is required' };
  }
  const trimmedName = name.trim();
  if (trimmedName.length === 0) {
    return { valid: false, error: 'API key name is required' };
  }
  if (trimmedName.length > 50) {
    return { valid: false, error: 'API key name must be 50 characters or less' };
  }
  if (trimmedName.length < 2) {
    return { valid: false, error: 'API key name must be at least 2 characters' };
  }
  if (!/^[a-zA-Z0-9_\-]+$/.test(trimmedName)) {
    return { valid: false, error: 'API key name can only contain letters, numbers, underscores, and hyphens' };
  }
  return { valid: true };
};

// Countries list
const COUNTRIES = {
  'AF': 'Afghanistan', 'AL': 'Albania', 'DZ': 'Algeria', 'AD': 'Andorra', 'AO': 'Angola',
  'AG': 'Antigua and Barbuda', 'AR': 'Argentina', 'AM': 'Armenia', 'AU': 'Australia',
  'AT': 'Austria', 'AZ': 'Azerbaijan', 'BS': 'Bahamas', 'BH': 'Bahrain', 'BD': 'Bangladesh',
  'BB': 'Barbados', 'BY': 'Belarus', 'BE': 'Belgium', 'BZ': 'Belize', 'BJ': 'Benin',
  'BT': 'Bhutan', 'BO': 'Bolivia', 'BA': 'Bosnia and Herzegovina', 'BW': 'Botswana',
  'BR': 'Brazil', 'BN': 'Brunei', 'BG': 'Bulgaria', 'BF': 'Burkina Faso', 'BI': 'Burundi',
  'CV': 'Cabo Verde', 'KH': 'Cambodia', 'CM': 'Cameroon', 'CA': 'Canada',
  'CF': 'Central African Republic', 'TD': 'Chad', 'CL': 'Chile', 'CN': 'China',
  'CO': 'Colombia', 'KM': 'Comoros', 'CG': 'Congo', 'CD': 'Congo (Democratic Republic)',
  'CR': 'Costa Rica', 'HR': 'Croatia', 'CU': 'Cuba', 'CY': 'Cyprus', 'CZ': 'Czech Republic',
  'DK': 'Denmark', 'DJ': 'Djibouti', 'DM': 'Dominica', 'DO': 'Dominican Republic',
  'EC': 'Ecuador', 'EG': 'Egypt', 'SV': 'El Salvador', 'GQ': 'Equatorial Guinea',
  'ER': 'Eritrea', 'EE': 'Estonia', 'SZ': 'Eswatini', 'ET': 'Ethiopia', 'FJ': 'Fiji',
  'FI': 'Finland', 'FR': 'France', 'GA': 'Gabon', 'GM': 'Gambia', 'GE': 'Georgia',
  'DE': 'Germany', 'GH': 'Ghana', 'GR': 'Greece', 'GD': 'Grenada', 'GT': 'Guatemala',
  'GN': 'Guinea', 'GW': 'Guinea-Bissau', 'GY': 'Guyana', 'HT': 'Haiti', 'HN': 'Honduras',
  'HU': 'Hungary', 'IS': 'Iceland', 'IN': 'India', 'ID': 'Indonesia', 'IR': 'Iran',
  'IQ': 'Iraq', 'IE': 'Ireland', 'IL': 'Israel', 'IT': 'Italy', 'JM': 'Jamaica',
  'JP': 'Japan', 'JO': 'Jordan', 'KZ': 'Kazakhstan', 'KE': 'Kenya', 'KI': 'Kiribati',
  'KP': 'Korea (North)', 'KR': 'Korea (South)', 'KW': 'Kuwait', 'KG': 'Kyrgyzstan',
  'LA': 'Laos', 'LV': 'Latvia', 'LB': 'Lebanon', 'LS': 'Lesotho', 'LR': 'Liberia',
  'LY': 'Libya', 'LI': 'Liechtenstein', 'LT': 'Lithuania', 'LU': 'Luxembourg',
  'MG': 'Madagascar', 'MW': 'Malawi', 'MY': 'Malaysia', 'MV': 'Maldives', 'ML': 'Mali',
  'MT': 'Malta', 'MH': 'Marshall Islands', 'MR': 'Mauritania', 'MU': 'Mauritius',
  'MX': 'Mexico', 'FM': 'Micronesia', 'MD': 'Moldova', 'MC': 'Monaco', 'MN': 'Mongolia',
  'ME': 'Montenegro', 'MA': 'Morocco', 'MZ': 'Mozambique', 'MM': 'Myanmar', 'NA': 'Namibia',
  'NR': 'Nauru', 'NP': 'Nepal', 'NL': 'Netherlands', 'NZ': 'New Zealand', 'NI': 'Nicaragua',
  'NE': 'Niger', 'NG': 'Nigeria', 'MK': 'North Macedonia', 'NO': 'Norway', 'OM': 'Oman',
  'PK': 'Pakistan', 'PW': 'Palau', 'PS': 'Palestine', 'PA': 'Panama', 'PG': 'Papua New Guinea',
  'PY': 'Paraguay', 'PE': 'Peru', 'PH': 'Philippines', 'PL': 'Poland', 'PT': 'Portugal',
  'QA': 'Qatar', 'RO': 'Romania', 'RU': 'Russia', 'RW': 'Rwanda', 'KN': 'Saint Kitts and Nevis',
  'LC': 'Saint Lucia', 'VC': 'Saint Vincent and the Grenadines', 'WS': 'Samoa',
  'SM': 'San Marino', 'ST': 'Sao Tome and Principe', 'SA': 'Saudi Arabia', 'SN': 'Senegal',
  'RS': 'Serbia', 'SC': 'Seychelles', 'SL': 'Sierra Leone', 'SG': 'Singapore',
  'SK': 'Slovakia', 'SI': 'Slovenia', 'SB': 'Solomon Islands', 'SO': 'Somalia',
  'ZA': 'South Africa', 'SS': 'South Sudan', 'ES': 'Spain', 'LK': 'Sri Lanka',
  'SD': 'Sudan', 'SR': 'Suriname', 'SE': 'Sweden', 'CH': 'Switzerland', 'SY': 'Syria',
  'TW': 'Taiwan', 'TJ': 'Tajikistan', 'TZ': 'Tanzania', 'TH': 'Thailand', 'TL': 'Timor-Leste',
  'TG': 'Togo', 'TO': 'Tonga', 'TT': 'Trinidad and Tobago', 'TN': 'Tunisia', 'TR': 'Turkey',
  'TM': 'Turkmenistan', 'TV': 'Tuvalu', 'UG': 'Uganda', 'UA': 'Ukraine',
  'AE': 'United Arab Emirates', 'GB': 'United Kingdom', 'US': 'United States',
  'UY': 'Uruguay', 'UZ': 'Uzbekistan', 'VU': 'Vanuatu', 'VA': 'Vatican City',
  'VE': 'Venezuela', 'VN': 'Vietnam', 'YE': 'Yemen', 'ZM': 'Zambia', 'ZW': 'Zimbabwe'
};

const MyAccount = () => {
  const notification = useNotification();
  const { handleAuthError } = useAuth();

  // Responsive detection
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // State management with granular loading states
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone_number: '',
    address_line1: '',
    address_city: '',
    address_state: '',
    address_postal_code: '',
    country: '',
    company_name: '',
    tax_id: ''
  });
  const [initialProfileData, setInitialProfileData] = useState({
    name: '',
    email: '',
    phone_number: '',
    address_line1: '',
    address_city: '',
    address_state: '',
    address_postal_code: '',
    country: '',
    company_name: '',
    tax_id: ''
  });
  const [profileFormErrors, setProfileFormErrors] = useState({});
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyFormData, setApiKeyFormData] = useState({ key_name: '' });
  const [apiKeyFormErrors, setApiKeyFormErrors] = useState({});
  
  // Granular loading states
  const [loadingStates, setLoadingStates] = useState({
    profile: false,
    savingProfile: false,
    apiKeys: false,
    creatingAPIKey: false,
    deletingAPIKey: null
  });
  
  // UI state
  const [showAPIKeyModal, setShowAPIKeyModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [selectedAPIKey, setSelectedAPIKey] = useState(null);
  const [newAPIKey, setNewAPIKey] = useState('');
  const [redirect, setRedirect] = useState(null);
  const [error, setError] = useState(null);
  const [lastShownError, setLastShownError] = useState('');

  const abortControllerRef = useRef(null);
  const lastSaveAttemptRef = useRef(0);
  const lastCreateAPIKeyAttemptRef = useRef(0);
  const SAVE_COOLDOWN = 2000;
  const CREATE_API_KEY_COOLDOWN = 2000;

  // Set Sentry context
  useEffect(() => {
    Sentry.setContext("my-account", {
      activeTab,
      isProfileComplete,
      apiKeysCount: apiKeys.length
    });
    
    logInfo('MyAccount component mounted', {
      activeTab
    });
    
    logUserAction('my_account_page_visited', {
      activeTab
    });
  }, [activeTab, isProfileComplete, apiKeys.length]);

  // Rate limiting checks
  const checkSaveRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastSave = now - lastSaveAttemptRef.current;
    
    if (timeSinceLastSave < SAVE_COOLDOWN) {
      logError('Save attempt blocked - rate limit exceeded', {
        timeSinceLastSave,
        cooldownPeriod: SAVE_COOLDOWN
      });
      return false;
    }
    
    lastSaveAttemptRef.current = now;
    return true;
  }, []);

  const checkCreateAPIKeyRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastCreate = now - lastCreateAPIKeyAttemptRef.current;
    
    if (timeSinceLastCreate < CREATE_API_KEY_COOLDOWN) {
      logError('API key creation blocked - rate limit exceeded', {
        timeSinceLastCreate,
        cooldownPeriod: CREATE_API_KEY_COOLDOWN
      });
      return false;
    }
    
    lastCreateAPIKeyAttemptRef.current = now;
    return true;
  }, []);

  // Request cancellation cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending requests');
      }
    };
  }, []);

  // Check for unsaved profile changes
  const hasUnsavedProfileChanges = useMemo(() => {
    return (
      profileData.name !== initialProfileData.name ||
      profileData.phone_number !== initialProfileData.phone_number ||
      profileData.address_line1 !== initialProfileData.address_line1 ||
      profileData.address_city !== initialProfileData.address_city ||
      profileData.address_state !== initialProfileData.address_state ||
      profileData.address_postal_code !== initialProfileData.address_postal_code ||
      profileData.country !== initialProfileData.country ||
      profileData.company_name !== initialProfileData.company_name ||
      profileData.tax_id !== initialProfileData.tax_id
    );
  }, [profileData, initialProfileData]);

  // Browser navigation warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedProfileChanges && activeTab === 'profile') {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedProfileChanges, activeTab]);

  // Memoized profile completion percentage
  const profileCompletionPercentage = useMemo(() => {
    const allFields = ['name', 'phone_number', 'address_line1', 'address_city', 'address_state', 'address_postal_code', 'country'];
    const completedFields = allFields.filter(field => !!profileData[field]);
    return Math.round((completedFields.length / allFields.length) * 100);
  }, [profileData]);

  // Memoized API key statistics
  const apiKeyStats = useMemo(() => {
    const totalKeys = apiKeys.length;
    const activeKeys = apiKeys.filter(key => key.is_active).length;
    const remainingSlots = Math.max(0, 10 - totalKeys);
    
    return { totalKeys, activeKeys, remainingSlots };
  }, [apiKeys]);

  // Memoized input validation
  const profileInputValidation = useMemo(() => ({
    name: validateName(profileData.name),
    phone_number: validatePhoneNumber(profileData.phone_number),
    address_line1: validateAddress(profileData.address_line1),
    address_postal_code: validatePostalCode(profileData.address_postal_code)
  }), [profileData.name, profileData.phone_number, profileData.address_line1, profileData.address_postal_code]);

  // Show error notifications
  useEffect(() => {
    if (error && error !== lastShownError) {
      setLastShownError(error);
    }
  }, [error, lastShownError]);

  // Clear all validation errors
  const clearAllValidationErrors = useCallback(() => {
    setProfileFormErrors({});
    setApiKeyFormErrors({});
  }, []);

const debouncedFetchProfile = useCallback(
  debounce(async () => {
    const startTime = performance.now();
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, profile: true }));
    setError(null);
    
    try {
      const response = await api.getUserProfile();
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (abortControllerRef.current?.signal.aborted) {
        return;
      }
      
      if (response.success && response.profile) {
        const sanitizedProfile = {
          name: sanitizeText(response.profile.name || ''),
          email: sanitizeText(response.profile.email || ''),
          phone_number: sanitizeText(response.profile.phone_number || ''),
          address_line1: sanitizeText(response.profile.address_line1 || ''),
          address_city: sanitizeText(response.profile.address_city || ''),
          address_state: sanitizeText(response.profile.address_state || ''),
          address_postal_code: sanitizeText(response.profile.address_postal_code || ''),
          country: response.profile.country || '',
          company_name: sanitizeText(response.profile.company_name || ''),
          tax_id: sanitizeText(response.profile.tax_id || '')
        };
        
        setProfileData(sanitizedProfile);
        setInitialProfileData(sanitizedProfile);
        
        const complete = !!(
          sanitizedProfile.name &&
          sanitizedProfile.address_line1 && 
          sanitizedProfile.address_postal_code && 
          sanitizedProfile.country
        );
        
        setIsProfileComplete(complete);
        
        logPerformance('fetch_profile', duration, {
          success: true,
          profileComplete: complete
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        
        logError('Profile fetch failed', {
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });
      }
   } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Check 401
      if (err.status === 401) {
        handleAuthError();
        return;
      }
      
      // Position 2: Check error code and use interceptor's message
      if (err.code) {
        const safeMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['PROFILE_FETCH_FAILED'];
        setError(safeMessage);
        logError('Profile fetch error', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 3: Fallback
      const safeMessage = err.error || ERROR_MESSAGES['PROFILE_FETCH_FAILED'];
      setError(safeMessage);
      
      logError('Error fetching profile', {
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, profile: false }));
    }
  }, 300),
  [handleAuthError]
);  // Manual retry function for profile fetch
  const retryFetchProfile = useCallback(() => {
    setError(null);
    debouncedFetchProfile();
  }, [debouncedFetchProfile]);

const debouncedFetchAPIKeys = useCallback(
  debounce(async () => {
    const startTime = performance.now();
    
    setLoadingStates(prev => ({ ...prev, apiKeys: true }));
    setError(null);

    try {
      const response = await api.listAPIKeys();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (response.success && response.api_keys) {
        const sanitizedKeys = response.api_keys.map(key => ({
          ...key,
          key_name: sanitizeText(key.key_name || ''),
          id: String(key.id)
        }));
        
        setApiKeys(sanitizedKeys);
        
        logPerformance('fetch_api_keys', duration, {
          success: true,
          keyCount: sanitizedKeys.length
        });
        
        logInfo('API keys loaded successfully', {
          count: sanitizedKeys.length,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        const safeMessage = getSafeErrorMessage(response);
        setError(safeMessage);
        
        logError('API keys fetch failed', {
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });
      }
   } catch (err) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Check 401
      if (err.status === 401) {
        handleAuthError();
        return;
      }
      
      // Position 2: Check error code and use interceptor's message
      if (err.code) {
        const safeMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['API_KEYS_FETCH_FAILED'];
        setError(safeMessage);
        logError('API keys fetch error', {
          code: err.code,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        return;
      }
      
      // Position 3: Fallback
      const safeMessage = err.error || ERROR_MESSAGES['API_KEYS_FETCH_FAILED'];
      setError(safeMessage);
      
      logError('Error fetching API keys', {
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoadingStates(prev => ({ ...prev, apiKeys: false }));
    }
  }, 300),
  [handleAuthError]
);  // Load data on mount and tab changes
  useEffect(() => {
    debouncedFetchProfile();
  }, [debouncedFetchProfile]);

  useEffect(() => {
    if (activeTab === 'api-keys') {
      debouncedFetchAPIKeys();
    }
  }, [activeTab, debouncedFetchAPIKeys]);

  // Check for redirect parameter
  useEffect(() => {
const params = new URLSearchParams(window.location.search);
    const redirectFrom = params.get('from');
    if (redirectFrom) {
      const safePath = validateRedirectPath(redirectFrom);
      setRedirect(safePath);
      
      logUserAction('redirected_to_account', {
        from: safePath
      });
    }
  }, []);

  // Strategic form error clearing
  const handleProfileInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    if (Object.keys(profileFormErrors).length > 0) {
      clearAllValidationErrors();
    }
    
    setProfileData(prev => ({ ...prev, [name]: value }));
  }, [profileFormErrors, clearAllValidationErrors]);

  const handleAPIKeyFormChange = useCallback((e) => {
    const { name, value } = e.target;
    
    if (Object.keys(apiKeyFormErrors).length > 0) {
      clearAllValidationErrors();
    }
    
    setApiKeyFormData(prev => ({ ...prev, [name]: value }));
  }, [apiKeyFormErrors, clearAllValidationErrors]);

  // Handle tab switching with unsaved changes warning
  const handleTabSwitch = useCallback((newTab) => {
    if (activeTab === 'profile' && hasUnsavedProfileChanges && newTab !== 'profile') {
      setPendingTab(newTab);
      setShowUnsavedChangesModal(true);
      
      logUserAction('unsaved_changes_modal_shown', {
        fromTab: activeTab,
        toTab: newTab
      });
      return;
    }
    
    setActiveTab(newTab);
  }, [activeTab, hasUnsavedProfileChanges]);

  // Confirm tab switch (discard changes)
  const handleConfirmTabSwitch = useCallback(() => {
    if (pendingTab) {
      logUserAction('tab_switch_with_unsaved_changes', {
        fromTab: activeTab,
        toTab: pendingTab
      });
      
      setActiveTab(pendingTab);
      setPendingTab(null);
      setShowUnsavedChangesModal(false);
    }
  }, [pendingTab, activeTab]);

  // Cancel tab switch (stay on profile)
  const handleCancelTabSwitch = useCallback(() => {
    logUserAction('tab_switch_cancelled_unsaved_changes', {
      fromTab: activeTab,
      toTab: pendingTab
    });
    
    setPendingTab(null);
    setShowUnsavedChangesModal(false);
  }, [activeTab, pendingTab]);

  // Cross-field validation
  const validateProfileForm = useCallback(() => {
    const errors = {};
    
    if (!profileInputValidation.name.valid) {
      errors.name = profileInputValidation.name.error;
    }
    
    if (!profileInputValidation.phone_number.valid) {
      errors.phone_number = profileInputValidation.phone_number.error;
    }
    
    if (!profileInputValidation.address_line1.valid) {
      errors.address_line1 = profileInputValidation.address_line1.error;
    }
    
    if (!profileInputValidation.address_postal_code.valid) {
      errors.address_postal_code = profileInputValidation.address_postal_code.error;
    }
    
    if (!profileData.country) {
      errors.country = "Country is required";
    }
    
    const hasAddressData = !!(
      profileData.address_line1 || 
      profileData.address_city || 
      profileData.address_state || 
      profileData.address_postal_code || 
      profileData.country
    );
    
    if (hasAddressData) {
      if (!profileData.address_line1) {
        errors.address_line1 = 'Address line is required when providing address information';
      }
      if (!profileData.address_postal_code) {
        errors.address_postal_code = 'Postal code is required when providing address information';
      }
      if (!profileData.country) {
        errors.country = 'Country is required when providing address information';
      }
    }
    
   // GSTIN validation for Indian users
    if (profileData.tax_id && profileData.country === 'IN') {
      const gstin = profileData.tax_id.trim().toUpperCase();
      const gstinPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstinPattern.test(gstin)) {
        errors.tax_id = 'Invalid GSTIN format. Expected format: 22AAAAA0000A1Z5';
      } else {
        const stateCode = parseInt(gstin.substring(0, 2), 10);
        const validStateCodes = [...Array.from({length: 37}, (_, i) => i + 1), 97];
        if (!validStateCodes.includes(stateCode)) {
          errors.tax_id = 'Invalid GSTIN: state code is not valid';
        }
      }
    }

    setProfileFormErrors(errors);
    
    const isValid = Object.keys(errors).length === 0;
    
    logInfo('Profile form validation completed', {
      isValid,
      errorCount: Object.keys(errors).length,
      errors: Object.keys(errors),
      hasAddressData
    });
    
    return isValid;
  }, [profileInputValidation, profileData.country, profileData.address_line1, profileData.address_city, profileData.address_state, profileData.address_postal_code, profileData.tax_id]);

const handleProfileSubmit = useCallback(async (e) => {
  e.preventDefault();
  
  logUserAction('profile_save_initiated', {
    profileComplete: isProfileComplete
  });
  
  if (!checkSaveRateLimit()) {
    notification.error(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
    return;
  }
  
  if (!validateProfileForm()) {
    return;
  }
  
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  
  abortControllerRef.current = new AbortController();
  setLoadingStates(prev => ({ ...prev, savingProfile: true }));
  
  try {
    const sanitizedData = {
      name: sanitizeText(profileData.name.trim()),
      phone_number: sanitizeText(profileData.phone_number.trim()),
      address_line1: sanitizeText(profileData.address_line1.trim()),
      address_city: sanitizeText(profileData.address_city.trim()),
      address_state: sanitizeText(profileData.address_state.trim()),
      address_postal_code: sanitizeText(profileData.address_postal_code.trim()),
      country: profileData.country,
      company_name: profileData.company_name ? sanitizeText(profileData.company_name.trim()) : '',
      tax_id: profileData.tax_id ? sanitizeText(profileData.tax_id.trim().toUpperCase()) : ''
    };
    
    const response = await api.updateUserProfile(sanitizedData);
    
    if (response.success) {
      setIsProfileComplete(response.is_profile_complete);
      setInitialProfileData(profileData);
      
      logInfo('Profile updated successfully', {
        profileComplete: response.is_profile_complete
      });
      
      logUserAction('profile_save_success', {
        profileComplete: response.is_profile_complete
      });

      if (redirect && response.is_profile_complete) {
        const safePath = validateRedirectPath(redirect);
        
        logUserAction('profile_complete_redirect_initiated', {
          redirectTo: safePath
        });
        
        setTimeout(() => {
          window.location.href = safePath;
        }, 2000);
      }
    } else {
      if (response.details?.fieldErrors) {
        setProfileFormErrors(response.details.fieldErrors);
      } else {
        const safeMessage = getSafeErrorMessage(response);
        notification.error(safeMessage);
      }
    }
 } catch (err) {
    if (err.name === 'AbortError') {
      return;
    }
    
    // Position 1: Check 401
    if (err.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check for field errors in interceptor-normalized error
    if (err.details?.fieldErrors) {
      setProfileFormErrors(err.details.fieldErrors);
      return;
    }
    
    // Position 3: Check error code and use interceptor's message
    if (err.code) {
      const safeMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['PROFILE_UPDATE_FAILED'];
      notification.error(safeMessage);
      
      logError('Profile update error', {
        code: err.code
      }, err);
      return;
    }
    
    // Position 4: Fallback
    const safeMessage = err.error || ERROR_MESSAGES['PROFILE_UPDATE_FAILED'];
    notification.error(safeMessage);
    
    logError('Error updating profile', {}, err);
  } finally {
    setLoadingStates(prev => ({ ...prev, savingProfile: false }));
  }
}, [profileData, validateProfileForm, checkSaveRateLimit, isProfileComplete, redirect, notification, handleAuthError]);  // Create API key with correct error handling order


const handleCreateAPIKey = useCallback(async (e) => {
  e.preventDefault();
  
  if (!checkCreateAPIKeyRateLimit()) {
    notification.warning(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
    return;
  }
  
  const nameValidation = validateAPIKeyName(apiKeyFormData.key_name);
  if (!nameValidation.valid) {
    setApiKeyFormErrors({ key_name: nameValidation.error });
    return;
  }
  
  setLoadingStates(prev => ({ ...prev, creatingAPIKey: true }));
  
  try {
    const sanitizedData = {
      key_name: sanitizeText(apiKeyFormData.key_name.trim())
    };
    
    const response = await api.createAPIKey(sanitizedData);
    
    if (response.success) {
      setNewAPIKey(response.api_key);
      setShowNewKeyModal(true);
      setShowAPIKeyModal(false);
      setApiKeyFormData({ key_name: '' });
      
      const newKey = {
        id: response.key_details.id,
        key_name: response.key_details.key_name,
        is_active: response.key_details.is_active,
        created_at: response.key_details.created_at,
        last_used_at: response.key_details.last_used_at
      };
      setApiKeys(prev => [...prev, newKey]);
      
      logUserAction('api_key_created', {
        keyName: sanitizedData.key_name,
        keyId: response.key_details.id
      });
    } else {
      if (response.code === 'INCOMPLETE_PROFILE') {
        notification.error('Please complete your profile before creating API keys');
        setActiveTab('profile');
      } else if (response.details?.fieldErrors) {
        setApiKeyFormErrors(response.details.fieldErrors);
      } else {
        const safeMessage = getSafeErrorMessage(response);
        notification.error(safeMessage);
      }
    }
  } catch (err) {
    // Position 1: Check 401
    if (err.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check for INCOMPLETE_PROFILE
    if (err.code === 'INCOMPLETE_PROFILE') {
      notification.error('Please complete your profile before creating API keys');
      setActiveTab('profile');
      return;
    }
    
    // Position 3: Check for field errors
    if (err.details?.fieldErrors) {
      setApiKeyFormErrors(err.details.fieldErrors);
      return;
    }
    
    // Position 4: Check error code and use interceptor's message
    if (err.code) {
      const safeMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['KEY_GENERATION_FAILED'];
      notification.error(safeMessage);
      
      logError('API key creation error', {
        code: err.code
      }, err);
      return;
    }
    
    // Position 5: Fallback
    const safeMessage = err.error || ERROR_MESSAGES['KEY_GENERATION_FAILED'];
    notification.error(safeMessage);
    
    logError('Error creating API key', {}, err);
  } finally {
    setLoadingStates(prev => ({ ...prev, creatingAPIKey: false }));
  }
}, [apiKeyFormData, checkCreateAPIKeyRateLimit, notification, handleAuthError]);
  // Delete API key with correct error handling order

  
 const handleDeleteAPIKey = useCallback(async (keyId) => {
  setLoadingStates(prev => ({ ...prev, deletingAPIKey: String(keyId) }));
  
  try {
    const response = await api.deleteAPIKey(String(keyId));
    
    if (response.success) {
      setApiKeys(prev => prev.filter(key => String(key.id) !== String(keyId)));
      setShowDeleteConfirm(false);
      setSelectedAPIKey(null);
      
      logUserAction('api_key_deleted', {
        keyId: String(keyId),
        keyName: response.deleted_key?.name
      });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      notification.error(safeMessage);
    }
  } catch (err) {
    // Position 1: Check 401
    if (err.status === 401) {
      handleAuthError();
      return;
    }
    
    // Position 2: Check error code and use interceptor's message
    if (err.code) {
      const safeMessage = err.error || ERROR_MESSAGES[err.code] || ERROR_MESSAGES['API_KEY_DELETE_FAILED'];
      notification.error(safeMessage);
      
      logError('API key deletion error', {
        code: err.code,
        keyId: String(keyId)
      }, err);
      return;
    }
    
    // Position 3: Fallback
    const safeMessage = err.error || ERROR_MESSAGES['API_KEY_DELETE_FAILED'];
    notification.error(safeMessage);
    
    logError('Error deleting API key', {
      keyId: String(keyId)
    }, err);
  } finally {
    setLoadingStates(prev => ({ ...prev, deletingAPIKey: null }));
  }
}, [handleAuthError, notification]);

  const handleCopyAPIKey = useCallback(async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      logUserAction('api_key_copied');
    } catch (err) {
      logError('Error copying API key', {}, err);
    }
  }, []);

  return (
    <>
      <div style={{ display: 'flex', background: '#000000', minHeight: '100vh', color: 'white' }}>
        {!isMobile && !isTablet && (
          <Sidebar 
            activePage="account" 
            isMobile={isMobile}
            isTablet={isTablet}
            isSidebarOpen={false}
            toggleSidebar={() => {}}
            authenticated={true}
            onAuthAction={() => {}}
          />
        )}
        
        <div style={{
          flex: 1,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          lineHeight: 1.6
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '40px 20px'
          }}>
            <div style={{
              background: '#121212',
              border: '1px solid #333333',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: '32px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#e0eacc',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#000',
                    fontWeight: '700',
                    fontSize: '16px',
                    marginRight: '24px'
                  }}>
                    {generateAvatar(profileData.name)}
                  </div>
                  <div>
                    <h1 style={{
                      fontSize: '20px',
                      fontWeight: '700',
                      marginBottom: '6px'
                    }}>
                      {profileData.name || 'User Account'}
                    </h1>
                    <p style={{
                      color: '#888',
                      fontSize: '14px'
                    }}>
                      Manage your profile and API access
                    </p>
                  </div>
                </div>
                
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  background: '#1a1a1a',
                  border: '1px solid #333333',
                  borderRadius: '8px',
                  padding: '6px'
                }}>
                  <button
                    onClick={() => handleTabSwitch('profile')}
                    style={{
                      padding: '10px 24px',
                      border: 'none',
                      background: activeTab === 'profile' ? '#e0eacc' : 'none',
                      color: activeTab === 'profile' ? '#000' : '#888',
                      fontWeight: '600',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '15px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      if (isProfileComplete) {
                        handleTabSwitch('api-keys');
                      }
                    }}
                    disabled={!isProfileComplete}
                    style={{
                      padding: '10px 24px',
                      border: 'none',
                      background: activeTab === 'api-keys' ? '#e0eacc' : 'none',
                      color: activeTab === 'api-keys' ? '#000' : !isProfileComplete ? '#555' : '#888',
                      fontWeight: '600',
                      borderRadius: '6px',
                      cursor: isProfileComplete ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                      fontSize: '15px',
                      whiteSpace: 'nowrap'
                    }}
                    title={!isProfileComplete ? "Complete your profile to access API keys" : ""}
                  >
                    {!isProfileComplete ? '◯ ' : ''}API Keys
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    color: '#ef4444',
                    marginRight: '12px',
                    flexShrink: 0,
                    fontSize: '18px'
                  }}>
                    ⚠
                  </span>
                  <span style={{
                    color: '#ef4444',
                    fontSize: '14px'
                  }}>
                    {error}
                  </span>
                </div>
                {(error === ERROR_MESSAGES['NETWORK_ERROR'] || error === ERROR_MESSAGES['CONNECTION_PROBLEM']) && (
                  <button
                    onClick={activeTab === 'profile' ? retryFetchProfile : debouncedFetchAPIKeys}
                    disabled={loadingStates.profile || loadingStates.apiKeys}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(238,85,85,0.05)',
                      border: '1px solid rgba(255,119,119,0.3)',
                      color: '#ff7777',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: (loadingStates.profile || loadingStates.apiKeys) ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                      marginLeft: '16px',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: (loadingStates.profile || loadingStates.apiKeys) ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!loadingStates.profile && !loadingStates.apiKeys) {
                        e.target.style.background = 'rgba(238,85,85,0.1)';
                        e.target.style.borderColor = 'rgba(255,85,85,0.5)';
                        e.target.style.color = '#ff5555';
                        e.target.style.boxShadow = 'inset 0 0 20px rgba(255,119,119,0.1)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!loadingStates.profile && !loadingStates.apiKeys) {
                        e.target.style.background = 'rgba(238,85,85,0.05)';
                        e.target.style.borderColor = 'rgba(255,119,119,0.3)';
                        e.target.style.color = '#ff7777';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <span style={{ marginRight: '8px', fontSize: '12px' }}>↻</span>
                    <span>{(loadingStates.profile || loadingStates.apiKeys) ? 'Retrying...' : 'Retry Now'}</span>
                  </button>
                )}
              </div>
            )}

            {redirect && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{
                  color: '#f59e0b',
                  marginRight: '12px',
                  flexShrink: 0,
                  fontSize: '18px'
                }}>
                  ⚠
                </span>
                <span style={{
                  color: '#f59e0b',
                  fontSize: '14px'
                }}>
                  Please complete your profile information to continue.
                </span>
              </div>
            )}

            {hasUnsavedProfileChanges && activeTab === 'profile' && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{
                  color: '#f59e0b',
                  marginRight: '12px',
                  flexShrink: 0,
                  fontSize: '18px'
                }}>
                  ⚠
                </span>
                <span style={{
                  color: '#f59e0b',
                  fontSize: '14px'
                }}>
                  You have unsaved changes. Click "Update Profile Data" to save your changes.
                </span>
              </div>
            )}

            {activeTab === 'profile' && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div style={{
                  background: '#121212',
                  border: '1px solid #333333',
                  borderRadius: '12px'
                }}>
                  <div style={{
                    background: '#1a1a1a',
                    padding: '24px 32px',
                    borderBottom: '1px solid #333333',
                    borderRadius: '12px 12px 0 0'
                  }}>
                    <h2 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#e0eacc'
                    }}>
                      Profile Configuration
                    </h2>
                  </div>
                  <div style={{ padding: '32px' }}>
                    {loadingStates.profile ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px'
                      }}>
                        <div style={{
                          border: '4px solid #333333',
                          borderTop: '4px solid #e0eacc',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{ marginLeft: '16px', color: '#888' }}>Loading profile data...</p>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'grid', gridTemplateRows: 'auto auto', gap: '40px' }}>
                          <div style={{
                            borderLeft: '4px solid #e0eacc',
                            paddingLeft: '20px'
                          }}>
                            <h3 style={{
                              fontSize: '14px',
                              fontWeight: '700',
                              color: '#fff',
                              marginBottom: '20px',
                              textTransform: 'uppercase',
                              letterSpacing: '1.5px'
                            }}>
                              Personal Data
                            </h3>
                            
                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                display: 'block',
                                fontWeight: '600',
                                color: '#ccc',
                                marginBottom: '8px',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Full Name <span style={{ color: '#e0eacc' }}>*</span>
                              </label>
                              <input
                                type="text"
                                name="name"
                                value={profileData.name || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^[A-Za-z\s\-']*$/.test(value)) {
                                    handleProfileInputChange(e);
                                  }
                                }}
                                placeholder="Enter your full name"
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  background: '#1a1a1a',
                                  border: `1px solid ${profileFormErrors.name ? '#ef4444' : '#333333'}`,
                                  borderRadius: '6px',
                                  color: 'white',
                                  fontSize: '15px',
                                  transition: 'all 0.2s'
                                }}
                                maxLength={100}
                                required
                              />
                              {profileFormErrors.name && (
                                <p style={{
                                  color: '#ef4444',
                                  fontSize: '12px',
                                  marginTop: '6px'
                                }}>
                                  {sanitizeText(profileFormErrors.name)}
                                </p>
                              )}
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                display: 'block',
                                fontWeight: '600',
                                color: '#ccc',
                                marginBottom: '8px',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Email Address
                              </label>
                              <input
                                type="email"
                                value={profileData.email || ''}
                                disabled
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  background: '#1a1a1a',
                                  border: '1px solid #333333',
                                  borderRadius: '6px',
                                  color: 'white',
                                  fontSize: '15px',
                                  opacity: 0.6,
                                  cursor: 'not-allowed'
                                }}
                              />
                              <p style={{
                                color: '#888',
                                fontSize: '12px',
                                marginTop: '6px'
                              }}>
                                Email cannot be changed
                              </p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                display: 'block',
                                fontWeight: '600',
                                color: '#ccc',
                                marginBottom: '8px',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Phone Number
                              </label>
                              <input
                                type="tel"
                                name="phone_number"
                                value={profileData.phone_number || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^[0-9+\s\-()]*$/.test(value)) {
                                    handleProfileInputChange(e);
                                  }
                                }}
                                placeholder="+1 (555) 123-4567"
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  background: '#1a1a1a',
                                  border: `1px solid ${profileFormErrors.phone_number ? '#ef4444' : '#333333'}`,
                                  borderRadius: '6px',
                                  color: 'white',
                                  fontSize: '15px',
                                  transition: 'all 0.2s'
                                }}
                                maxLength={20}
                              />
                              {profileFormErrors.phone_number && (
                                <p style={{
                                  color: '#ef4444',
                                  fontSize: '12px',
                                  marginTop: '6px'
                                }}>
                                  {sanitizeText(profileFormErrors.phone_number)}
                                </p>
                              )}
                            </div>
                          </div>

                          <div style={{
                            borderLeft: '4px solid #e0eacc',
                            paddingLeft: '20px'
                          }}>
                            <h3 style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#fff',
                              marginBottom: '20px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Billing Address
                            </h3>
                            
                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                display: 'block',
                                fontWeight: '600',
                                color: '#ccc',
                                marginBottom: '8px',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Address Line <span style={{ color: '#e0eacc' }}>*</span>
                              </label>
                              <input
                                type="text"
                                name="address_line1"
                                value={profileData.address_line1 || ''}
                                onChange={handleProfileInputChange}
                                placeholder="Street address"
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  background: '#1a1a1a',
                                  border: `1px solid ${profileFormErrors.address_line1 ? '#ef4444' : '#333333'}`,
                                  borderRadius: '6px',
                                  color: 'white',
                                  fontSize: '15px',
                                  transition: 'all 0.2s'
                                }}
                                maxLength={255}
                                required
                              />
                              {profileFormErrors.address_line1 && (
                                <p style={{
                                  color: '#ef4444',
                                  fontSize: '12px',
                                  marginTop: '6px'
                                }}>
                                  {sanitizeText(profileFormErrors.address_line1)}
                                </p>
                              )}
                            </div>

                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '16px',
                              marginBottom: '20px'
                            }}>
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontWeight: '600',
                                  color: '#ccc',
                                  marginBottom: '8px',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  City
                                </label>
                                <input
                                  type="text"
                                  name="address_city"
                                  value={profileData.address_city || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^[A-Za-z\s\-.']*$/.test(value)) {
                                      handleProfileInputChange(e);
                                    }
                                  }}
                                  placeholder="City"
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: '#1a1a1a',
                                    border: '1px solid #333333',
                                    borderRadius: '6px',
                                    color: 'white',
                                    fontSize: '15px',
                                    transition: 'all 0.2s'
                                  }}
                                  maxLength={100}
                                />
                              </div>
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontWeight: '600',
                                  color: '#ccc',
                                  marginBottom: '8px',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  State
                                </label>
                                <input
                                  type="text"
                                  name="address_state"
                                  value={profileData.address_state || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^[A-Za-z\s\-]*$/.test(value)) {
                                      handleProfileInputChange(e);
                                    }
                                  }}
                                  placeholder="State"
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: '#1a1a1a',
                                    border: '1px solid #333333',
                                    borderRadius: '6px',
                                    color: 'white',
                                    fontSize: '15px',
                                    transition: 'all 0.2s'
                                  }}
                                  maxLength={100}
                                />
                              </div>
                            </div>

                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '16px'
                            }}>
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontWeight: '600',
                                  color: '#ccc',
                                  marginBottom: '8px',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Postal Code <span style={{ color: '#e0eacc' }}>*</span>
                                </label>
                                <input
                                  type="text"
                                  name="address_postal_code"
                                  value={profileData.address_postal_code || ''}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^[A-Za-z0-9\-\s]*$/.test(value)) {
                                      handleProfileInputChange(e);
                                    }
                                  }}
                                  placeholder="12345"
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: '#1a1a1a',
                                    border: `1px solid ${profileFormErrors.address_postal_code ? '#ef4444' : '#333333'}`,
                                    borderRadius: '6px',
                                    color: 'white',
                                    fontSize: '15px',
                                    transition: 'all 0.2s'
                                  }}
                                  maxLength={20}
                                  required
                                />
                                {profileFormErrors.address_postal_code && (
                                  <p style={{
                                    color: '#ef4444',
                                    fontSize: '12px',
                                    marginTop: '6px'
                                  }}>
                                    {sanitizeText(profileFormErrors.address_postal_code)}
                                  </p>
                                )}
                              </div>
                              <div>
                                <label style={{
                                  display: 'block',
                                  fontWeight: '600',
                                  color: '#ccc',
                                  marginBottom: '8px',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Country <span style={{ color: '#e0eacc' }}>*</span>
                                </label>
                                <select
                                  name="country"
                                  value={profileData.country || ''}
                                  onChange={handleProfileInputChange}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: '#1a1a1a',
                                    border: `1px solid ${profileFormErrors.country ? '#ef4444' : '#333333'}`,
                                    borderRadius: '6px',
                                    color: 'white',
                                    fontSize: '15px',
                                    transition: 'all 0.2s'
                                  }}
                                  required
                                >
                                  <option value="">Select Country</option>
                                  {Object.entries(COUNTRIES).map(([code, name]) => (
                                    <option key={code} value={code}>
                                      {name}
                                    </option>
                                  ))}
                                </select>
                                {profileFormErrors.country && (
                                  <p style={{
                                    color: '#ef4444',
                                    fontSize: '12px',
                                    marginTop: '6px'
                                  }}>
                                    {sanitizeText(profileFormErrors.country)}
                                  </p>
                                )}
                              </div>
                         </div>
                          </div>

                          <div style={{
                            borderLeft: '4px solid #e0eacc',
                            paddingLeft: '20px'
                          }}>
                            <h3 style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#fff',
                              marginBottom: '20px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              Business Information
                            </h3>

                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                display: 'block',
                                fontWeight: '600',
                                color: '#ccc',
                                marginBottom: '8px',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Company Name
                              </label>
                              <input
                                type="text"
                                name="company_name"
                                value={profileData.company_name || ''}
                                onChange={handleProfileInputChange}
                                placeholder="Your company or organization name"
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  background: '#1a1a1a',
                                  border: '1px solid #333333',
                                  borderRadius: '6px',
                                  color: 'white',
                                  fontSize: '15px',
                                  transition: 'all 0.2s'
                                }}
                                maxLength={200}
                              />
                              <p style={{
                                color: '#888',
                                fontSize: '12px',
                                marginTop: '6px'
                              }}>
                                Optional — used on invoices if provided
                              </p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                              <label style={{
                                display: 'block',
                                fontWeight: '600',
                                color: '#ccc',
                                marginBottom: '8px',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Tax ID {profileData.country === 'IN' ? '(GSTIN)' : ''}
                              </label>
                              <input
                                type="text"
                                name="tax_id"
                                value={profileData.tax_id || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^[A-Za-z0-9\-]*$/.test(value)) {
                                    handleProfileInputChange(e);
                                  }
                                }}
                                placeholder={profileData.country === 'IN' ? '22AAAAA0000A1Z5' : 'Your tax identification number'}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  background: '#1a1a1a',
                                  border: `1px solid ${profileFormErrors.tax_id ? '#ef4444' : '#333333'}`,
                                  borderRadius: '6px',
                                  color: 'white',
                                  fontSize: '15px',
                                  transition: 'all 0.2s',
                                  textTransform: 'uppercase'
                                }}
                                maxLength={30}
                              />
                              {profileFormErrors.tax_id && (
                                <p style={{
                                  color: '#ef4444',
                                  fontSize: '12px',
                                  marginTop: '6px'
                                }}>
                                  {sanitizeText(profileFormErrors.tax_id)}
                                </p>
                              )}
                              <p style={{
                                color: '#888',
                                fontSize: '12px',
                                marginTop: '6px'
                              }}>
                                {profileData.country === 'IN' 
                                  ? 'Optional — 15-character GSTIN for tax invoices' 
                                  : 'Optional — used on invoices if provided'}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <div style={{
                    background: '#121212',
                    border: '1px solid #333333',
                    borderRadius: '12px',
                    padding: '24px',
                    textAlign: 'center'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      marginBottom: '16px',
                      color: '#e0eacc'
                    }}>
                      Completion Progress
                    </h3>
                    <div style={{
                      width: '120px',
                      height: '120px',
                      margin: '0 auto 16px',
                      background: `conic-gradient(#e0eacc ${profileCompletionPercentage}%, #333 ${profileCompletionPercentage}%)`,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: '#121212',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        fontWeight: '700',
                        color: '#e0eacc'
                      }}>
                        {profileCompletionPercentage}%
                      </div>
                    </div>
                    <p style={{
                      color: '#888',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Complete required fields for full access
                    </p>
                  </div>

                  <div style={{
                    background: '#121212',
                    border: '1px solid #333333',
                    borderRadius: '12px',
                    padding: '24px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      marginBottom: '16px',
                      color: '#e0eacc'
                    }}>
                      Account Status
                    </h3>
            
                    <div style={{
                      background: isProfileComplete ? 'rgba(34, 197, 94, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                      border: `1px solid ${isProfileComplete ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                      color: isProfileComplete ? '#22c55e' : '#f59e0b',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      lineHeight: 1.4,
                      marginBottom: '16px'
                    }}>
                      {isProfileComplete ? 'Profile complete - API access enabled' : 'Complete all required fields to activate API access'}
                    </div>
                    <button
                      onClick={handleProfileSubmit}
                      disabled={loadingStates.savingProfile}
                      style={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #e0eacc 0%, #c8d9a9 100%)',
                        color: '#000',
                        border: 'none',
                        padding: '16px 32px',
                        borderRadius: '8px',
                        fontWeight: '700',
                        fontSize: '15px',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        opacity: loadingStates.savingProfile ? 0.6 : 1
                      }}
                    >
                      {loadingStates.savingProfile ? 'Saving...' : 'Update Profile Data'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'api-keys' && isProfileComplete && (
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div style={{
                  background: '#121212',
                  border: '1px solid #333333',
                  borderRadius: '12px'
                }}>
                  <div style={{
                    background: '#1a1a1a',
                    padding: '24px 32px',
                    borderBottom: '1px solid #333333',
                    borderRadius: '12px 12px 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <h2 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#e0eacc'
                    }}>
                      API Key Management
                    </h2>
                    <button
                      onClick={() => setShowAPIKeyModal(true)}
                      style={{
                        background: 'linear-gradient(135deg, #e0eacc 0%, #c8d9a9 100%)',
                        color: '#000',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      + Create API Key
                    </button>
                  </div>
                  <div style={{ padding: '32px' }}>
                    {loadingStates.apiKeys ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px'
                      }}>
                        <div style={{
                          border: '4px solid #333333',
                          borderTop: '4px solid #e0eacc',
                          borderRadius: '50%',
                          width: '40px',
                          height: '40px',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{ marginLeft: '16px', color: '#888' }}>Loading API keys...</p>
                      </div>
                    ) : apiKeys.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '60px 32px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginBottom: '16px'
                        }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: 'rgba(224,234,204,0.1)',
                            border: '1px solid rgba(224,234,204,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#e0eacc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                            </svg>
                          </div>
                        </div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '600',
                          color: '#fff',
                          marginBottom: '8px'
                        }}>
                          No API Keys
                        </h3>
                        <p style={{
                          fontSize: '14px',
                          color: '#888',
                          marginBottom: '24px',
                          lineHeight: 1.5
                        }}>
                          Create your first API key to start using our programmatic interface
                        </p>
                        <button
                          onClick={() => setShowAPIKeyModal(true)}
                          style={{
                            background: 'linear-gradient(135deg, #e0eacc 0%, #c8d9a9 100%)',
                            color: '#000',
                            border: 'none',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.3s'
                          }}
                        >
                          + Create API Key
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {apiKeys.map((apiKey) => (
                          <div key={apiKey.id} style={{
                            background: '#1a1a1a',
                            border: '1px solid #333333',
                            borderRadius: '8px',
                            padding: '24px',
                            transition: 'all 0.2s',
                            opacity: loadingStates.deletingAPIKey === String(apiKey.id) ? 0.5 : 1
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '12px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <h3 style={{
                                  fontSize: '18px',
                                  fontWeight: '600',
                                  color: '#fff',
                                  marginRight: '12px'
                                }}>
                                  {sanitizeText(apiKey.key_name)}
                                </h3>
                                <div style={{
                                  background: apiKey.is_active ? 'rgba(224, 234, 204, 0.12)' : 'rgba(239, 68, 68, 0.1)',
                                  border: apiKey.is_active ? '1px solid rgba(224, 234, 204, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                                  color: apiKey.is_active ? '#e0eacc' : '#ef4444',
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  {apiKey.is_active ? 'Active' : 'Inactive'}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedAPIKey(apiKey);
                                  setShowDeleteConfirm(true);
                                }}
                                disabled={loadingStates.deletingAPIKey === String(apiKey.id)}
                                style={{
                                  padding: '8px 12px',
                                  border: '1px solid #333333',
                                  background: '#222',
                                  color: '#888',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  cursor: loadingStates.deletingAPIKey === String(apiKey.id) ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s',
                                  opacity: loadingStates.deletingAPIKey === String(apiKey.id) ? 0.5 : 1
                                }}
                                onMouseOver={(e) => {
                                  if (loadingStates.deletingAPIKey !== String(apiKey.id)) {
                                    e.target.style.borderColor = '#ef4444';
                                    e.target.style.color = '#ef4444';
                                  }
                                }}
                                onMouseOut={(e) => {
                                  if (loadingStates.deletingAPIKey !== String(apiKey.id)) {
                                    e.target.style.borderColor = '#333333';
                                    e.target.style.color = '#888';
                                  }
                                }}
                              >
                                {loadingStates.deletingAPIKey === String(apiKey.id) ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '16px'
                            }}>
                              <div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#888',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  marginBottom: '4px'
                                }}>
                                  Created
                                </div>
                                <div style={{
                                  fontSize: '14px',
                                  color: '#ccc'
                                }}>
                                  {new Date(apiKey.created_at).toLocaleDateString()}
                                </div>
                              </div>
                              <div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#888',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  marginBottom: '4px'
                                }}>
                                  Last Used
                                </div>
                                <div style={{
                                  fontSize: '14px',
                                  color: '#ccc'
                                }}>
                                  {apiKey.last_used_at ? new Date(apiKey.last_used_at).toLocaleDateString() : 'Never'}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {apiKeys.length > 0 ? (
                    <>
                      <div style={{
                        background: '#121212',
                        border: '1px solid #333333',
                        borderRadius: '12px',
                        padding: '24px'
                      }}>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          marginBottom: '16px',
                          color: '#e0eacc'
                        }}>
                          Key Management
                        </h3>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px',
                          paddingBottom: '12px',
                          borderBottom: '1px solid #333'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Total Keys
                          </span>
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#e0eacc'
                          }}>
                            {apiKeyStats.totalKeys}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px',
                          paddingBottom: '12px',
                          borderBottom: '1px solid #333'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Active Keys
                          </span>
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#e0eacc'
                          }}>
                            {apiKeyStats.activeKeys}
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Remaining Slots
                          </span>
                          <span style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#e0eacc'
                          }}>
                            {apiKeyStats.remainingSlots}
                          </span>
                        </div>
                      </div>

                      <div style={{
                        background: '#121212',
                        border: '1px solid #333333',
                        borderRadius: '12px',
                        padding: '24px'
                      }}>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          marginBottom: '16px',
                          color: '#e0eacc'
                        }}>
                          Account Limits
                        </h3>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Max API Keys
                          </span>
                          <span style={{
                            fontSize: '14px',
                            color: '#ccc'
                          }}>
                            10
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Key Name Length
                          </span>
                          <span style={{
                            fontSize: '14px',
                            color: '#ccc'
                          }}>
                            2-50 chars
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{
                        background: '#121212',
                        border: '1px solid #333333',
                        borderRadius: '12px',
                        padding: '24px'
                      }}>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          marginBottom: '16px',
                          color: '#e0eacc'
                        }}>
                          Getting Started
                        </h3>
                        <div style={{
                          color: '#888',
                          fontSize: '14px',
                          lineHeight: 1.5
                        }}>
                          <p style={{ marginBottom: '12px' }}>
                            API keys provide secure access to your account resources.
                          </p>
                          <p style={{ marginBottom: '12px' }}>
                            Each key can be independently managed and revoked.
                          </p>
                          <p>
                            Keep your API keys secure and never share them publicly.
                          </p>
                        </div>
                      </div>

                      <div style={{
                        background: '#121212',
                        border: '1px solid #333333',
                        borderRadius: '12px',
                        padding: '24px'
                      }}>
                        <h3 style={{
                          fontSize: '16px',
                          fontWeight: '700',
                          marginBottom: '16px',
                          color: '#e0eacc'
                        }}>
                          Account Limits
                        </h3>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Max API Keys
                          </span>
                          <span style={{
                            fontSize: '14px',
                            color: '#ccc'
                          }}>
                            10
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Key Name Length
                          </span>
                          <span style={{
                            fontSize: '14px',
                            color: '#ccc'
                          }}>
                            2-50 chars
                          </span>
                        </div>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{
                            fontSize: '14px',
                            color: '#888'
                          }}>
                            Allowed Characters
                          </span>
                          <span style={{
                            fontSize: '14px',
                            color: '#ccc'
                          }}>
                            a-z, 0-9, _, -
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showUnsavedChangesModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            background: '#121212',
            border: '1px solid #444',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            width: '100%',
            maxWidth: '500px',
            padding: '32px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'rgba(245, 158, 11, 0.2)',
                border: '2px solid rgba(245, 158, 11, 0.5)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#f59e0b',
                fontSize: '24px',
                flexShrink: 0
              }}>
                ⚠
              </div>
              <div>
                <h3 style={{
                  color: '#fff',
                  fontSize: '20px',
                  fontWeight: '700',
                  marginBottom: '8px'
                }}>
                  Unsaved Changes
                </h3>
                <p style={{
                  color: '#aaa',
                  fontSize: '14px',
                  lineHeight: 1.6
                }}>
                  You have unsaved changes in your profile. Are you sure you want to leave? Your changes will be lost.
                </p>
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCancelTabSwitch}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: 'none',
                  background: '#1a1a1a',
                  border: '1px solid #444',
                  color: '#ccc'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#222';
                  e.target.style.borderColor = '#555';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#1a1a1a';
                  e.target.style.borderColor = '#444';
                }}
              >
                Stay on Profile
              </button>
              <button
                onClick={handleConfirmTabSwitch}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: 'none',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#ef4444'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.25)';
                  e.target.style.borderColor = 'rgba(239, 68, 68, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.15)';
                  e.target.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                }}
              >
                Switch Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {showAPIKeyModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50
        }}>
          <div style={{
            background: '#121212',
            border: '1px solid #333333',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '460px'
          }}>
            <div style={{ padding: '29px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '22px'
              }}>
                <h3 style={{
                  fontWeight: '600',
                  color: 'white',
                  fontSize: '18px'
                }}>
                  Create API Key
                </h3>
                <button
                  onClick={() => {
                    setShowAPIKeyModal(false);
                    setApiKeyFormData({ key_name: '' });
                    setApiKeyFormErrors({});
                  }}
                  style={{
                    color: '#888',
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.color = 'white'}
                  onMouseOut={(e) => e.target.style.color = '#888'}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleCreateAPIKey}>
                <div style={{ marginBottom: '22px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '7px',
                    color: '#ccc',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    Key Name <span style={{ color: '#e0eacc' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="key_name"
                    value={apiKeyFormData.key_name}
                    onChange={handleAPIKeyFormChange}
                    placeholder="Enter API key name"
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: '#1a1a1a',
                      border: `2px solid ${apiKeyFormErrors.key_name ? '#ef4444' : '#333333'}`,
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      if (!apiKeyFormErrors.key_name) {
                        e.target.style.borderColor = '#e0eacc';
                      }
                    }}
                    onBlur={(e) => {
                      if (!apiKeyFormErrors.key_name) {
                        e.target.style.borderColor = '#333333';
                      }
                    }}
                    maxLength={50}
                    required
                  />
                  {apiKeyFormErrors.key_name && (
                    <p style={{
                      color: '#ef4444',
                      fontSize: '12px',
                      marginTop: '6px'
                    }}>
                      {sanitizeText(apiKeyFormErrors.key_name)}
                    </p>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '29px'
                }}>
                  <button
                    type="submit"
                    disabled={loadingStates.creatingAPIKey}
                    style={{
                      padding: '14px 22px',
                      background: 'linear-gradient(135deg, #e0eacc 0%, #c8d9a9 100%)',
                      color: '#000',
                      border: 'none',
                      fontWeight: '600',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '14px',
                      opacity: loadingStates.creatingAPIKey ? 0.6 : 1
                    }}
                  >
                    {loadingStates.creatingAPIKey ? 'Creating...' : 'Create API Key'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showNewKeyModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50
        }}>
          <div style={{
            background: '#121212',
            border: '1px solid #333333',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '512px'
          }}>
            <div style={{ padding: '32px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  fontWeight: '600',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  Your New API Key
                </h3>
                <button
                  onClick={() => {
                    setShowNewKeyModal(false);
                    setNewAPIKey('');
                  }}
                  style={{
                    color: '#888',
                    background: 'none',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    transition: 'color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.color = 'white'}
                  onMouseOut={(e) => e.target.style.color = '#888'}
                >
                  ×
                </button>
              </div>

              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{
                    color: '#f59e0b',
                    marginRight: '12px',
                    marginTop: '2px',
                    flexShrink: 0
                  }}>
                    ⚠
                  </span>
                  <div>
                    <p style={{
                      color: '#f59e0b',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      Critical Security Notice
                    </p>
                    <p style={{
                      color: '#f59e0b',
                      fontSize: '14px',
                      marginTop: '4px'
                    }}>
                      This is the ONLY time your full API key will be displayed. Copy it now and store it securely. You cannot retrieve it later.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#ccc',
                  fontWeight: '600',
                  fontSize: '16px'
                }}>
                  API Key (Full)
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <input
                    type="text"
                    value={newAPIKey}
                    readOnly
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#1a1a1a',
                      border: '1px solid #333333',
                      borderRadius: '6px',
                      color: '#ccc',
                      fontSize: '14px',
                      fontFamily: 'monospace'
                    }}
                  />
                  <button
                    onClick={() => handleCopyAPIKey(newAPIKey)}
                    style={{
                      background: 'linear-gradient(135deg, #e0eacc 0%, #c8d9a9 100%)',
                      color: '#000',
                      border: 'none',
                      padding: '12px 16px',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    title="Copy Full API Key"
                  >
                    ✓
                  </button>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowNewKeyModal(false);
                  setNewAPIKey('');
                }}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: 'linear-gradient(135deg, #e0eacc 0%, #c8d9a9 100%)',
                  color: '#000',
                  border: 'none',
                  fontWeight: '600',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '16px'
                }}
              >
                I've Saved My API Key
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedAPIKey && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 50
        }}>
          <div style={{
            background: '#121212',
            border: '1px solid #333333',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
            width: '100%',
            maxWidth: '420px',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #2a2a2a'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                <h3 style={{
                  fontWeight: '600',
                  color: '#fff',
                  margin: 0,
                  fontSize: '16px'
                }}>
                  Delete API Key
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedAPIKey(null);
                }}
                disabled={loadingStates.deletingAPIKey === String(selectedAPIKey.id)}
                style={{
                  padding: '8px',
                  borderRadius: '6px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#6b7280',
                  cursor: loadingStates.deletingAPIKey === String(selectedAPIKey.id) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div style={{ padding: '20px' }}>
              <p style={{
                color: '#9ca3af',
                marginBottom: '16px',
                marginTop: 0,
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                Are you sure you want to delete this API key?
              </p>
              
              {/* Key Name Box */}
              <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #2a2a2a',
                borderRadius: '8px',
                padding: '12px 14px',
                marginBottom: '16px'
              }}>
                <span style={{ color: '#6b7280', fontSize: '11px', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>
                  API Key Name
                </span>
                <span style={{ color: '#e0eacc', fontSize: '14px', fontWeight: '500' }}>
                  {sanitizeText(selectedAPIKey.key_name)}
                </span>
              </div>
              
              {/* Warning Box */}
              <div style={{
                backgroundColor: 'rgba(255,119,119,0.05)',
                border: '1px solid rgba(255,119,119,0.2)',
                borderRadius: '8px',
                padding: '10px 12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080" style={{ flexShrink: 0 }}>
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </svg>
                <span style={{ color: '#ff8080', fontSize: '12px' }}>
                  This action cannot be undone. Any applications using this key will stop working.
                </span>
              </div>
              
              {/* Delete Button */}
              <button
                onClick={() => handleDeleteAPIKey(selectedAPIKey.id)}
                disabled={loadingStates.deletingAPIKey === String(selectedAPIKey.id)}
                style={{
                  width: '100%',
                  backgroundColor: loadingStates.deletingAPIKey === String(selectedAPIKey.id) ? '#4a3030' : 'rgba(255,100,100,0.15)',
                  border: '1px solid rgba(255,119,119,0.4)',
                  color: '#ff7777',
                  borderRadius: '8px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px 20px',
                  fontSize: '14px',
                  cursor: loadingStates.deletingAPIKey === String(selectedAPIKey.id) ? 'not-allowed' : 'pointer',
                  gap: '8px'
                }}
              >
                {loadingStates.deletingAPIKey === String(selectedAPIKey.id) ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTopColor: '#ff7777',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                    Delete API Key
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default MyAccount;